import { api, type Page } from './client'

export interface Company {
  id: number
  code: string
  name: string
  vatNumber?: string
  email?: string
  phone?: string
  status: string
  assignedAgentId?: number
  createdAt: string
  mergedIntoCompanyId?: number | null
  mergedAt?: string | null
  /** F-095: quando il record e' stato spostato nel cestino (`null` = attivo). */
  deletedAt?: string | null
  deletedBy?: number | null
  deleteReason?: string | null
}

/** `GET /api/companies/{id}` returns the full `Company` entity (see `CompaniesController.Get`,
 * backend) rather than a hand-picked projection, plus two extra fields present only when the
 * record was merged away (`CompaniesController.cs`: `canonicalCompanyId`/`canonicalResolution`,
 * the live record at the end of the merge chain). */
export interface CompanyDetail extends Company {
  slug?: string
  legalForm?: string
  fiscalCode?: string
  sdiCode?: string
  pec?: string
  address?: string
  city?: string
  province?: string
  postalCode?: string
  country?: string
  notes?: string
  updatedAt?: string | null
  canonicalCompanyId?: number
  canonicalResolution?: string
}

export interface CompanyRequest {
  name: string
  vatNumber?: string
  fiscalCode?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  province?: string
  notes?: string
  assignedAgentId?: number
  /** TASK-0078: mirrors `CompanyDetail.sdiCode`/`pec`/`postalCode`/`country` (already readable —
   * this is what makes them writable, both at creation and edit). */
  sdiCode?: string
  pec?: string
  postalCode?: string
  country?: string
  forceCreate?: boolean
}

/** Body of a successful `POST /api/companies` (`DuplicateResponses.Created`, backend) — always
 * carries `possibleDuplicates` (possibly empty), populated only with phone-only matches since a
 * CF/PIVA/Email match 409s instead unless `forceCreate` was set. */
export interface CompanyCreateResult {
  id: number
  possibleDuplicates: { entityType: string; id: number; matchedField: string }[]
}

/** Body of a successful `POST /api/companies/{id}/merge` (`CompaniesController.Merge`, backend). */
export interface CompanyMergeResult {
  winnerCompanyId: number
  loserCompanyId: number
  fieldsFilledFromLoser: string[]
  mergedAt: string
}

export const companiesApi = {
  list: (p: { search?: string; page?: number; size?: number; includeMerged?: boolean; sort?: string } = {}) => {
    const q = new URLSearchParams()
    if (p.search) q.set('search', p.search)
    q.set('page', String(p.page ?? 1))
    q.set('pageSize', String(p.size ?? 25))
    if (p.includeMerged) q.set('includeMerged', 'true')
    if (p.sort) q.set('sort', p.sort)
    return api<Page<Company>>(`/api/companies?${q}`)
  },
  get: (id: number) => api<CompanyDetail>(`/api/companies/${id}`),
  create: (data: CompanyRequest) =>
    api<CompanyCreateResult>('/api/companies', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: CompanyRequest) =>
    api<void>(`/api/companies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  /** Admin-gated on the backend (`[Authorize(PolicyNames.Admin)]`) — callers must check
   * `auth.role` before offering this in the UI (see `CompanyDetailPage`). */
  merge: (winnerId: number, loserCompanyId: number, motivation?: string) =>
    api<CompanyMergeResult>(`/api/companies/${winnerId}/merge`, {
      method: 'POST',
      body: JSON.stringify({ loserCompanyId, motivation }),
    }),
  /** Admin-gated on the backend (`[Authorize(PolicyNames.Admin)]`); not surfaced in the UI yet. */
  remove: (id: number) => api<void>(`/api/companies/${id}`, { method: 'DELETE' }),
}
